import React, { ChangeEvent, DragEvent, useEffect, useId, useRef, useState } from "react";

import { XboxSpinner } from "@gamedev-web/gamedev-sdk-core";
import { BasicMultilineTextInput, Icon, IconSize } from "@gaming/xds-react-js";
import { Attach, Clock, Delete, Download, Photo, Upload } from "@gaming/xds-react-js-icons";

import { Modal, ModalInitialFocus } from "../../components/Modal/Modal";
import { useStrings } from "../../localization/localizationHelpers";
import * as styles from "./PartnerSettingsPage.module.css";

type LegalEntityNameChangeModalProps = Readonly<{
  isOpen: boolean;
  requiresDocument: boolean;
  requiresReview: boolean;
  isSubmitting: boolean;
  error: string;
  allowedExtensions: readonly string[];
  maxDocumentBytes: number;
  onConfirm: (document?: File, description?: string) => void;
  onCancel: () => void;
}>;

const descriptionMaxLength = 1000;

export const LegalEntityNameChangeModal = ({
  isOpen,
  requiresDocument,
  requiresReview,
  isSubmitting,
  error,
  allowedExtensions,
  maxDocumentBytes,
  onConfirm,
  onCancel,
}: LegalEntityNameChangeModalProps) => {
  const titleId = useId();
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [document, setDocument] = useState<File>();
  const [documentError, setDocumentError] = useState("");
  const [description, setDescription] = useState("");
  const [
    confirmationTitle,
    confirmationDescription,
    reviewTitle,
    reviewDescription,
    unavailableTitle,
    unavailableDevKits,
    unavailableSigning,
    reviewDuration,
    documentLabel,
    documentHint,
    dropzoneLabel,
    invalidDocument,
    downloadLabel,
    deleteLabel,
    descriptionLabel,
    optionalLabel,
    cancelLabel,
    confirmLabel,
    submitLabel,
  ] = useStrings(
    "partnerSettingsLegalNameConfirmationTitle",
    "partnerSettingsLegalNameConfirmationDescription",
    "partnerSettingsLegalNameReviewTitle",
    "partnerSettingsLegalNameReviewDescription",
    "legalEntityNameChangeReviewModalUnavailableTitle",
    "legalEntityNameChangeReviewModalDevKits",
    "legalEntityNameChangeReviewModalSigning",
    "legalEntityNameChangeReviewModalDuration",
    "partnerSettingsLegalNameDocumentLabel",
    "partnerSettingsLegalNameDocumentHint",
    "partnerSettingsLegalNameDropzone",
    "partnerSettingsLegalNameInvalidDocument",
    "devGuideDownloadLink",
    "deleteButton",
    "partnerSettingsLegalNameDescriptionLabel",
    "optional",
    "cancelButton",
    "partnerSettingsLegalNameConfirmLabel",
    "partnerSettingsLegalNameSubmitLabel"
  );
  const actionLabel = requiresReview ? submitLabel : confirmLabel;

  useEffect(() => {
    if (!isOpen) {
      setDocument(undefined);
      setDocumentError("");
      setDescription("");
    }
  }, [isOpen]);

  const selectDocument = (selectedDocument?: File) => {
    const extension = selectedDocument?.name
      .slice(selectedDocument.name.lastIndexOf("."))
      .toLowerCase();
    if (
      !selectedDocument ||
      !extension ||
      !allowedExtensions.includes(extension) ||
      selectedDocument.size > maxDocumentBytes
    ) {
      setDocument(undefined);
      setDocumentError(invalidDocument);
      if (documentInputRef.current) {
        documentInputRef.current.value = "";
      }
      return;
    }

    setDocument(selectedDocument);
    setDocumentError("");
  };

  const handleDocumentChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectDocument(event.target.files?.[0]);
  };

  const handleDocumentDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    if (!isSubmitting) {
      selectDocument(event.dataTransfer.files[0]);
    }
  };

  const removeDocument = () => {
    setDocument(undefined);
    setDocumentError("");
    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
  };

  const downloadDocument = () => {
    if (!document) {
      return;
    }
    const url = URL.createObjectURL(document);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = document.name;
    link.click();
    URL.revokeObjectURL(url);
  };

  const documentMetadata = document
    ? `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
        document.size / (1024 * 1024)
      )} MB · ${new Date(document.lastModified).toLocaleDateString()}`
    : "";

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSubmitting ? () => undefined : onCancel}
      ariaLabelledBy={titleId}
      initialFocus={requiresDocument ? ModalInitialFocus.FirstInput : ModalInitialFocus.FirstButton}
      closeOnOutsideClick={!isSubmitting}
      width={560}
    >
      <div className={styles.legalNameModal} aria-busy={isSubmitting}>
        <h2 id={titleId} className={styles.legalNameModalTitle}>
          {requiresReview ? reviewTitle : confirmationTitle}
        </h2>
        <p className={styles.legalNameModalDescription}>
          {requiresReview ? reviewDescription : confirmationDescription}
        </p>
        {requiresDocument && (
          <div className={styles.reviewImpact}>
            <p>{unavailableTitle}</p>
            <ul>
              <li>{unavailableDevKits}</li>
              <li>{unavailableSigning}</li>
            </ul>
            <div className={styles.reviewDuration}>
              <Icon aria-hidden="true" icon={Clock} iconSize={IconSize.Custom} />
              <span>{reviewDuration}</span>
            </div>
          </div>
        )}
        {requiresDocument && (
          <div className={styles.documentField}>
            <label className={styles.documentLabel} htmlFor="legal-name-supporting-document">
              {documentLabel}
              <span className={styles.requiredIndicator}> *</span>
            </label>
            {document ? (
              <div className={styles.documentRow}>
                <Icon
                  aria-hidden="true"
                  className={styles.documentTypeIcon}
                  icon={/\.(png|jpe?g)$/i.test(document.name) ? Photo : Attach}
                  iconSize={IconSize.Custom}
                />
                <span className={styles.documentName}>{document.name}</span>
                <span className={styles.documentMetadata}>{documentMetadata}</span>
                <div className={styles.documentActions}>
                  <button
                    type="button"
                    className={styles.documentRemoveButton}
                    aria-label={downloadLabel}
                    disabled={isSubmitting}
                    onClick={downloadDocument}
                  >
                    <Icon aria-hidden="true" icon={Download} iconSize={IconSize.Custom} />
                  </button>
                  <button
                    type="button"
                    className={styles.documentRemoveButton}
                    aria-label={deleteLabel}
                    disabled={isSubmitting}
                    onClick={removeDocument}
                  >
                    <Icon aria-hidden="true" icon={Delete} iconSize={IconSize.Custom} />
                  </button>
                </div>
              </div>
            ) : (
              <label
                className={styles.documentDropzone}
                htmlFor="legal-name-supporting-document"
                onDragOver={event => event.preventDefault()}
                onDrop={handleDocumentDrop}
              >
                <Icon aria-hidden="true" icon={Upload} iconSize={IconSize.Custom} />
                <span>{dropzoneLabel}</span>
                <input
                  ref={documentInputRef}
                  id="legal-name-supporting-document"
                  className={styles.documentInput}
                  type="file"
                  accept={allowedExtensions.join(",")}
                  disabled={isSubmitting}
                  onChange={handleDocumentChange}
                  required
                />
              </label>
            )}
            <span className={styles.documentHint}>{documentHint}</span>
            {documentError && <span className={styles.documentError}>{documentError}</span>}
          </div>
        )}
        {requiresDocument && (
          <div className={styles.legalNameDescriptionField}>
            <label className={styles.legalNameDescriptionLabel} htmlFor="legal-name-description">
              {descriptionLabel} ({optionalLabel})
            </label>
            <BasicMultilineTextInput
              id="legal-name-description"
              name="legal-name-description"
              value={description}
              onChange={event => setDescription(event.target.value)}
              className={styles.legalNameDescriptionInput}
              inputClassName={styles.legalNameDescriptionTextArea}
              disabled={isSubmitting}
              maxLength={descriptionMaxLength}
            />
          </div>
        )}
        {error && (
          <p className={styles.modalError} role="alert">
            {error}
          </p>
        )}
        <div className={styles.legalNameModalActions}>
          <button
            type="button"
            className={styles.cancelButton}
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={styles.saveButton}
            aria-label={actionLabel}
            disabled={isSubmitting || (requiresDocument && !document)}
            onClick={() => onConfirm(document, description.trim() || undefined)}
          >
            {actionLabel}
          </button>
        </div>
        {isSubmitting && (
          <div className={styles.legalNameModalBusyOverlay} role="status" aria-label={actionLabel}>
            <XboxSpinner />
          </div>
        )}
      </div>
    </Modal>
  );
};
